import type { Theme } from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'
import MuiTooltip from '@material-ui/core/Tooltip'
import CancelIcon from '@material-ui/icons/Cancel'

import ListIcon from '@material-ui/icons/List'
import {
  useClientOptions,
  ActionButton,
  CustomBadge,
  Tooltip,
  Typography,
  FormSectionTitle,
  Hidden,
  useUser
} from '@paintscout/ui'
import type { WithUseStyles, Action } from '@paintscout/ui'

import type {
  UnitAbbreviation,
  LabelsOption,
  QuoteItemSection,
  RenderableItemSubstrate,
  UpdateableArea
} from '@paintscout/util/builder'
import { calculateDimensionValues, getAreaSubstrates, getFeatures, getObjectLabels } from '@paintscout/util/builder'
import get from 'lodash/get'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React, { useCallback } from 'react'
import ItemTable from '../../../ItemTable'
import AreaSubstrateHeaderCell from './AreaSubstrateHeaderCell'
import AreaSubstrateItemCell from './AreaSubstrateItemCell'
import TotalsColumns from './TotalsColumns'

export interface AreaSubstratesProps extends WithUseStyles<typeof useStyles> {
  quote?: QuoteDocument
  options?: OptionsDocument
  area?: UpdateableArea
  section?: QuoteItemSection
  showAddButton?: boolean
  showTotals?: boolean
  actions?: Action[]
  label?: string
  sublabel?: string
  hideEmpty?: boolean
  tourIds?: boolean
  onAddClick?: (event: React.MouseEvent<HTMLElement>) => void
  onItemActionClick?: (
    event: React.MouseEvent<HTMLElement>,
    action: string,
    item: RenderableItemSubstrate,
    section: QuoteItemSection
  ) => void
  onReorder?: (args: { items: any[]; section: QuoteItemSection }) => void
  onItemClick?: (event: React.MouseEvent<HTMLElement>, item: RenderableItemSubstrate) => void
  onTotalsClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export interface AreaSubstratesState {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginBottom: `${theme.spacing(4)}px`
  },
  tableHead: {
    height: theme.typography.pxToRem(36),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('md')]: {
      fontSize: '0.75em'
    }
  },
  actionColumn: {
    paddingLeft: `${theme.spacing(0.5)}px !important`,
    paddingRight: `${theme.spacing(0.5)}px !important`
  },
  itemName: {
    ...theme.typography.subtitle2,
    display: 'block'
  },
  itemClientLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 0,
    ...theme.typography.body1
  },
  formSection: {},

  labelIcon: {
    fontSize: '1.0em',
    marginLeft: theme.spacing() / 2
  },
  sublabel: {},
  name: {
    display: 'grid'
  },
  mobileExtra: {
    ...theme.typography.body1,
    width: '100%'
  },
  warningIcon: {
    fontSize: '1rem'
  },
  row: {
    display: 'contents',
    '&:hover': {
      '& td': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    }
  }
}))

function AreaSubstrates(props: AreaSubstratesProps) {
  const {
    area,
    showAddButton,
    section,
    showTotals,
    quote,
    options,
    label,
    sublabel,
    hideEmpty,
    tourIds,
    onItemActionClick,
    onTotalsClick
  } = props
  const classes = useStyles(props)
  const { isViewer, isCrew } = useUser()
  const actions = props?.actions.filter(Boolean) ?? []

  const objectLabels = getObjectLabels({ options })
  const features = getFeatures({ options })
  const hasMaterialCost = get(features, 'quotes.materialCost.enabled', false)

  const items: RenderableItemSubstrate[] = getAreaSubstrates({
    quote,
    options,
    areaKey: area.key,
    section,
    overrideOptions: { showPrices: true, showQuantities: false },
    showGroups: true
  })

  const renderHeadColumns = useCallback(
    () => (
      <>
        <Hidden smDown>
          <AreaSubstrateHeaderCell>Description</AreaSubstrateHeaderCell>
          <AreaSubstrateHeaderCell align="right">Qty</AreaSubstrateHeaderCell>
          <AreaSubstrateHeaderCell align="right">{`${objectLabels.prep.value} (hr)`}</AreaSubstrateHeaderCell>
          {hasMaterialCost && (
            <AreaSubstrateHeaderCell align="right">{`${objectLabels.labour.value} (hr)`}</AreaSubstrateHeaderCell>
          )}
          <AreaSubstrateHeaderCell align="right">Total (hr)</AreaSubstrateHeaderCell>
          {hasMaterialCost && (
            <AreaSubstrateHeaderCell align="right">{`Materials (${objectLabels.currency.symbol})`}</AreaSubstrateHeaderCell>
          )}
          <AreaSubstrateHeaderCell align="right">{`Labor (${objectLabels.currency.symbol}`})</AreaSubstrateHeaderCell>
          {hasMaterialCost && (
            <AreaSubstrateHeaderCell align="right">{`Total (${objectLabels.currency.symbol})`}</AreaSubstrateHeaderCell>
          )}
        </Hidden>
        <Hidden mdUp>
          <AreaSubstrateHeaderCell>{objectLabels.substrate.value}</AreaSubstrateHeaderCell>
        </Hidden>
        {/*  actions column */}
        {/* don't set isControl prop on this cell or else drag & drop will resize the columns when dragging */}
        <AreaSubstrateHeaderCell className={classes.actionColumn} />
      </>
    ),
    []
  )

  const renderRowColumns = useCallback(
    (item: RenderableItemSubstrate) => {
      const materials = (item.materials.useCustom ? item.materials.custom : item.materials.default) || 0
      const hours = item.hours.useCustom ? item.hours.custom : item.hours.default
      const prep = typeof item.prep === 'string' ? parseFloat(item.prep) : item.prep
      const price = item.price.useCustom ? item.price.custom : item.price.default
      const totalHours = prep + hours
      const totalPrice = price + materials

      // Disallow moving items if they are in a substrate group
      const rowActions = item.inSubstrateGroup
        ? actions.filter(
            (action) => !['make-bid', 'make-option', 'make-pending', 'archive', 'delete'].includes(action.key)
          )
        : [...actions]

      return (
        <tr className={classes.row}>
          {/* desktop */}
          <Hidden smDown>
            <AreaSubstrateItemCell>
              <SubstrateName item={item} area={area} objectLabels={objectLabels} />
            </AreaSubstrateItemCell>
            <AreaSubstrateItemCell align="right" width="small">
              {item.rateType === 'quantity' ? (
                <Typography format={'quantity'} value={item.quantity} variant={'body1'} />
              ) : null}
            </AreaSubstrateItemCell>
            <AreaSubstrateItemCell align="right" isPrep width="small">
              {prep ? <Typography format={'hours'} value={prep} variant={'body1'} /> : null}
            </AreaSubstrateItemCell>
            {hasMaterialCost && (
              <AreaSubstrateItemCell align="right" isPrep width="large">
                <CustomBadge showBadge={item.hours.useCustom}>
                  <Typography format={'hours'} value={hours} variant={'body1'} />
                </CustomBadge>
              </AreaSubstrateItemCell>
            )}
            <AreaSubstrateItemCell align="right" isPrep width="small">
              <Typography format={'hours'} value={totalHours} variant={'body1'} />
            </AreaSubstrateItemCell>
            {hasMaterialCost && (
              <AreaSubstrateItemCell align="right" width="large">
                <CustomBadge showBadge={item.materials.useCustom}>
                  <Typography format={'price'} value={materials} variant={'body1'} />
                </CustomBadge>
              </AreaSubstrateItemCell>
            )}
            <AreaSubstrateItemCell align="right" width="large">
              <CustomBadge showBadge={item.price.useCustom}>
                <Typography format={'price'} value={price} variant={'body1'} />
              </CustomBadge>
            </AreaSubstrateItemCell>
            {hasMaterialCost && (
              <AreaSubstrateItemCell align="right" width="large">
                <Typography value={totalPrice} format={'price'} variant={'body1'} />
              </AreaSubstrateItemCell>
            )}
          </Hidden>

          {/* mobile */}
          <Hidden mdUp>
            <AreaSubstrateItemCell>
              <SubstrateName item={item} area={area} objectLabels={objectLabels} />
              <div className={classes.mobileExtra}>{item.quantity && <span>Qty: {item.quantity}</span>}</div>
              <div className={classes.mobileExtra}>
                <span>
                  {!!prep || prep === 0 ? ` Prep ${Math.round(prep * 100) / 100} /` : ''}
                  {!!hours || hours === 0 ? ` Painting ${Math.round(hours * 100) / 100} /` : ''}
                  {totalHours || hours === 0 ? ` Total ${Math.round(totalHours * 100) / 100}` : ''}
                </span>
              </div>
              <div className={classes.mobileExtra}>
                {price && (
                  <span>
                    <Typography value={totalPrice} format={'price'} variant={'body1'} showUnits />
                  </span>
                )}
              </div>
            </AreaSubstrateItemCell>
          </Hidden>

          {/* both */}
          <AreaSubstrateItemCell
            cancelDrag={true}
            align="right"
            isControl
            width="small"
            className={classes.actionColumn}
          >
            <ActionButton
              disabled={!rowActions.length}
              actions={rowActions}
              onActionClick={(ev, action) => {
                onItemActionClick(ev, action, item, section)
              }}
            />
          </AreaSubstrateItemCell>
        </tr>
      )
    },
    [hasMaterialCost, area]
  )

  const renderTotalRowColumns = useCallback(() => {
    const { totals: areaTotals } = area

    return (
      <TotalsColumns
        prep={areaTotals.prep}
        hours={areaTotals.hours}
        price={areaTotals.price}
        materials={areaTotals.materials}
        showMaterials={hasMaterialCost}
        onClick={!isViewer && !isCrew ? onTotalsClick : null}
      />
    )
  }, [area, hasMaterialCost, onTotalsClick])

  if (items.length === 0 && hideEmpty) {
    return null
  }

  return (
    <>
      <Tooltip badgeTop={'33%'} content={sublabel} title={objectLabels.substrate.plural}>
        <FormSectionTitle variant="h4" title={label} />
      </Tooltip>
      <ItemTable
        section={section}
        classes={{
          tableHead: classes.tableHead,
          root: classes.root
        }}
        showAddButton={showAddButton}
        addButtonLabel={`Add ${objectLabels.substrate.value}`}
        addButtonDraggable={false}
        onAddClick={props.onAddClick}
        items={items}
        checkboxes={false}
        tourIds={tourIds}
        onItemClick={
          ['bid', 'additional', 'added-options', 'pending'].includes(section) && isCrew ? null : props.onItemClick
        }
        onReorder={({ items }) => {
          props.onReorder({ items, section })
        }}
        renderHeadColumns={renderHeadColumns}
        renderRowColumns={renderRowColumns}
        renderTotalRowColumns={showTotals && renderTotalRowColumns}
        onTotalsClick={!isViewer && !isCrew ? onTotalsClick : null}
      />
    </>
  )
}

function SubstrateName({
  item,
  area,
  objectLabels
}: {
  item: RenderableItemSubstrate
  area: UpdateableArea
  objectLabels: LabelsOption
}) {
  const classes = useStyles({})
  const { options } = useClientOptions()
  const { unit } = getObjectLabels({ options })
  const dimension = getDimension(item, area, unit.abbreviation)

  return (
    <Typography variant={'body2'} component="div">
      {item.clientLabel && item.name !== item.clientLabel ? (
        <div className={classes.name}>
          <div className={classes.itemClientLabel}>
            {item.clientLabel}
            {item.workOrderOnly && (
              <MuiTooltip title={'Hidden from customer - Only shows on work order'}>
                <CancelIcon className={classes.labelIcon} />
              </MuiTooltip>
            )}
            {item.inSubstrateGroup && (
              <MuiTooltip
                title={`Not totalled and cannot be moved - Part of a ${objectLabels.substrate.value.toLowerCase()} group`}
              >
                <ListIcon className={classes.labelIcon} />
              </MuiTooltip>
            )}
            {item.inAreaGroup && (
              <MuiTooltip title={'Part of an area group - cannot be moved to options'}>
                <ListIcon className={classes.labelIcon} />
              </MuiTooltip>
            )}
          </div>
          <div className={classes.itemName}>{[item.name, dimension].filter((item) => item).join(' - ')}</div>
        </div>
      ) : (
        <div className={classes.name}>
          <div className={classes.itemClientLabel}>
            {item.name}
            {item.workOrderOnly && (
              <MuiTooltip title={'Hidden from customer - only shows on work order'}>
                <CancelIcon className={classes.labelIcon} />
              </MuiTooltip>
            )}
            {item.inSubstrateGroup && (
              <MuiTooltip title={`Not totalled - Part of a ${objectLabels.substrate.value.toLowerCase()} group`}>
                <ListIcon className={classes.labelIcon} />
              </MuiTooltip>
            )}
          </div>
          <div className={classes.itemName}>{dimension ? `${dimension}` : ''}</div>
        </div>
      )}
    </Typography>
  )
}

function getDimension(item: RenderableItemSubstrate, area: UpdateableArea, unit: UnitAbbreviation): string {
  const { rateType } = item
  const { areaType, dimensions } = area

  if (rateType === 'quantity') {
    return `${item.quantity}`
  }

  let lnft = dimensions['lnft']?.useCustom ? dimensions['lnft'].custom : dimensions['lnft']?.default ?? 0
  let sqft = dimensions['sqft']?.useCustom ? dimensions['sqft'].custom : dimensions['sqft']?.default ?? 0
  let sqftWalls = dimensions['sqftWalls']?.useCustom
    ? dimensions['sqftWalls'].custom
    : dimensions['sqftWalls']?.default ?? 0
  let sqftFloor = dimensions['sqftFloor']?.useCustom
    ? dimensions['sqftFloor'].custom
    : dimensions['sqftFloor']?.default ?? 0
  let sqftCeiling = dimensions['sqftCeiling']?.useCustom
    ? dimensions['sqftCeiling'].custom
    : dimensions['sqftCeiling']?.default ?? 0
  let _cuft = dimensions['cuft']?.useCustom ? dimensions['cuft'].custom : dimensions['cuft']?.default ?? 0

  if (item.length.useCustom || item.height.useCustom || item.width.useCustom) {
    const length = item.length.useCustom ? item.length.custom : item.length.default
    const height = item.height.useCustom ? item.height.custom : item.height.default
    const width = item.width.useCustom ? item.width.custom : item.width.default
    const {
      lnft: lnftValue,
      sqft: sqftValue,
      sqftWalls: sqftWallsValue,
      sqftFloor: sqftFloorValue,
      sqftCeiling: sqftCeilingValue,
      cuft: cuftValue
    } = calculateDimensionValues({
      length,
      width,
      height,
      isRoom: areaType === 'room',
      useInches: false
    })
    lnft = lnftValue
    sqft = sqftValue
    sqftWalls = sqftWallsValue
    sqftFloor = sqftFloorValue
    sqftCeiling = sqftCeilingValue
    _cuft = cuftValue
  }

  switch (rateType) {
    case 'sqft_walls':
    case 'sqftWalls':
      return `${toFixed(item.dimensionOverride || (areaType === 'room' ? sqftWalls : sqft))}${unit.square}`
    case 'sqft_ceiling':
    case 'sqftCeiling':
      return `${toFixed(item.dimensionOverride || (areaType === 'room' ? sqftCeiling : sqft))}${unit.square}`
    case 'sqft_floor':
    case 'sqftFloor':
      return `${toFixed(item.dimensionOverride || (areaType === 'room' ? sqftFloor : sqft))}${unit.square}`
    case 'lnft':
      return `${toFixed(item.dimensionOverride || lnft)}${unit.linear}`
    default:
      return `${toFixed(item.dimensionOverride || sqft)}${unit.square}`
  }
}

function toFixed(val: number) {
  if (parseFloat(val.toFixed(2)) !== parseFloat(val.toFixed())) {
    return val.toFixed(2)
  } else {
    return val
  }
}

export default AreaSubstrates
